import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { cameraExchangeLogsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import type { CameraExchangeLog } from '@/types'

// #059 — superuser "Camera Logs": persisted camera<->server exchange source of
// truth. Backend (CameraExchangeLogViewSet) is the real gate (IsAdminUser);
// this page is also behind <SuperuserRoute>.

// #060 — only kinds the middleware actually produces today. `params` is the
// legacy reportback (zero hits ever in prod) and `other` is never classified,
// so they were dropdown noise.
const KINDS = ['', 'setcode', 'menu_txt_download', 'status', 'image']
const POLL_MS = 10_000

export function CameraLogsPage() {
  const [imei, setImei] = useState('')
  const [kind, setKind] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<number | null>(null)

  const params = { imei: imei.trim() || undefined, kind: kind || undefined, page }
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.cameraExchangeLogs(params),
    queryFn: () => cameraExchangeLogsApi.list(params),
    refetchInterval: POLL_MS,
    placeholderData: (prev) => prev,
  })

  const rows = data?.results ?? []
  const total = data?.count ?? 0
  const pages = Math.max(1, Math.ceil(total / 20))

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* #126 — nav bar shows "Camera Logs"; keep only the dynamic telemetry span. */}
        <span className="text-xs text-muted-foreground">
          {isFetching ? 'refreshing…' : `${total} exchanges`} · live (every {POLL_MS / 1000}s)
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <input
            value={imei}
            onChange={(e) => { setImei(e.target.value); setPage(1) }}
            placeholder="Filter IMEI"
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          />
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value); setPage(1) }}
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          >
            {KINDS.map((k) => <option key={k} value={k}>{k || 'all kinds'}</option>)}
          </select>
          <button onClick={() => refetch()} className="rounded border border-border px-2 py-1 text-sm">
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load logs. Are you a superuser?</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No exchanges match.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2">Received</th>
                <th className="px-3 py-2">IMEI</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Menu?</th>
                <th className="px-3 py-2">Resp bytes</th>
                <th className="px-3 py-2">ms</th>
                <th className="px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: CameraExchangeLog) => (
                <RowDetail key={r.id} row={r} open={open === r.id}
                           onToggle={() => setOpen(open === r.id ? null : r.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3 text-sm">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-1 disabled:opacity-40">Prev</button>
        <span>Page {page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-1 disabled:opacity-40">Next</button>
      </div>
    </div>
  )
}

function RowDetail({ row, open, onToggle }:
  { row: CameraExchangeLog; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer border-t border-border hover:bg-muted/30">
        <td className="px-3 py-2 whitespace-nowrap">{new Date(row.received_at).toLocaleString()}</td>
        <td className="px-3 py-2 font-mono text-xs">{row.imei}</td>
        <td className="px-3 py-2">{row.kind}</td>
        <td className="px-3 py-2">{row.status_code ?? '—'}</td>
        <td className="px-3 py-2">{row.menu_present ? 'yes' : '—'}</td>
        <td className="px-3 py-2">{row.response_bytes}</td>
        <td className="px-3 py-2">{row.latency_ms ?? '—'}</td>
        <td className="px-3 py-2 font-mono text-xs">{row.client_ip || '—'}</td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={8} className="px-3 py-3 space-y-3">
            <Field label="Path">{row.path}</Field>
            <Field label="Response body (served)"><pre className="whitespace-pre-wrap">{row.response_body || '—'}</pre></Field>
            <Field label="Menu.txt content (uploaded)"><pre className="whitespace-pre-wrap">{row.menu_content || '—'}</pre></Field>
            <Field label="Serve-time context (why this body)">
              <pre className="whitespace-pre-wrap">
                {row.server_context ? JSON.stringify(row.server_context, null, 2) : '—'}
              </pre>
            </Field>
            <Field label="Request summary"><pre className="whitespace-pre-wrap">{row.request_summary || '—'}</pre></Field>
          </td>
        </tr>
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground">{label}</div>
      <div className="font-mono text-xs">{children}</div>
    </div>
  )
}
